import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchInvitePreview } from "./utils/inviteUtils";

export default function JoinLanding() {
  const navigate = useNavigate();

  // ?error= is set by the post-auth handler in App.js after a failed redemption.
  // Resolve it synchronously — the component always re-mounts on navigation so
  // window.location.search is always current.
  const urlError = new URLSearchParams(window.location.search).get("error");

  const urlErrorMessage =
    urlError === "invalid"
      ? "This invite link is invalid. Ask your band admin for a new one."
      : urlError === "expired"
      ? "This invite link has expired. Ask your band admin for a new one."
      : urlError === "network"
      ? "Something went wrong. Please try your invite link again."
      : null;

  // Skip the loading tick entirely when we already have a URL-level error —
  // there's nothing async to resolve and no auth CTAs to show.
  const [loading, setLoading] = useState(() => !urlError);
  const [bandName, setBandName] = useState("");
  const [sessionErrorMessage, setSessionErrorMessage] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  useEffect(() => {
    if (urlError) return; // URL error takes priority; skip session reads.

    const hydrateInviteContext = async () => {
      const urlCode = (new URLSearchParams(window.location.search).get("code") || "").trim().toUpperCase();
      const storedCode = (sessionStorage.getItem("pendingInviteCode") || "").trim().toUpperCase();
      const resolvedCode = urlCode || storedCode;

      if (!resolvedCode) {
        setSessionErrorMessage("This invite link is invalid. Ask your band admin for a new one.");
        setLoading(false);
        return;
      }

      setInviteCode(resolvedCode);

      const preview = await fetchInvitePreview(resolvedCode);

      if (preview.inviteStatus === "valid") {
        setBandName(preview.bandName || "");
        setSessionErrorMessage("");
        sessionStorage.setItem("pendingInviteCode", resolvedCode);
        sessionStorage.setItem("pendingInviteBandName", preview.bandName || "");
        sessionStorage.removeItem("pendingInviteStatus");
      } else if (preview.inviteStatus === "expired") {
        setBandName("");
        setSessionErrorMessage("This invite link has expired. Ask your band admin for a new one.");
        sessionStorage.removeItem("pendingInviteCode");
        sessionStorage.removeItem("pendingInviteBandName");
        sessionStorage.setItem("pendingInviteStatus", "expired");
      } else {
        setBandName("");
        setSessionErrorMessage("This invite link is invalid or has already been used. Ask your band admin for a new one.");
        sessionStorage.removeItem("pendingInviteCode");
        sessionStorage.removeItem("pendingInviteBandName");
        sessionStorage.setItem("pendingInviteStatus", "invalid");
      }

      setLoading(false);
    };

    hydrateInviteContext();
  }, [urlError]);

  const goTo = (mode) => {
    const path = mode === "signup" ? "/signup" : "/login";
    navigate(path, { state: { inviteMode: true, bandName, inviteCode } });
  };

  // ── loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center px-5 py-8">
        <div className="w-full max-w-[420px] bg-surface border border-border border-t-2 border-t-brand-gold rounded-sm px-12 py-14 shadow-[0_24px_80px_rgba(0,0,0,0.7)]">
          <div className="font-display text-[1.1rem] tracking-[0.25em] uppercase text-brand-gold mb-2">
            Band OS
          </div>
          <p className="text-text-secondary text-[0.88rem]">Loading invite…</p>
        </div>
      </div>
    );
  }

  // ── error (URL-level from post-auth redirect, or session-level from pre-auth
  //    lookup) — no auth CTAs shown in either case ───────────────────────────────
  const errorMessage = urlErrorMessage || sessionErrorMessage;
  if (errorMessage) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center px-5 py-8">
        <div className="w-full max-w-[420px] bg-surface border border-border border-t-2 border-t-brand-gold rounded-sm px-12 py-14 shadow-[0_24px_80px_rgba(0,0,0,0.7)]">
          <div className="font-display text-[1.1rem] tracking-[0.25em] uppercase text-brand-gold mb-2">
            Band OS
          </div>
          <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-2">
            Invite unavailable
          </h1>
          <div className="bg-red-950/50 border border-red-800/50 rounded-sm text-[#e07070] text-[0.88rem] px-4 py-3.5 leading-[1.6] mb-4">
            {errorMessage}
          </div>
        </div>
      </div>
    );
  }

  // ── valid invite ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-base flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-[420px] bg-surface border border-border border-t-2 border-t-brand-gold rounded-sm px-12 py-14 shadow-[0_24px_80px_rgba(0,0,0,0.7)]">
        <div className="font-display text-[1.1rem] tracking-[0.25em] uppercase text-brand-gold mb-2">
          Band OS
        </div>
        <h1 className="font-display text-[2rem] font-normal tracking-[-0.02em] text-text-primary mb-2">
          {bandName ? (
            <>
              You've been invited to join{" "}
              <span className="text-brand-gold">{bandName}</span>
            </>
          ) : (
            "You've been invited to join a band"
          )}
        </h1>
        <p className="text-text-secondary text-[0.88rem] mb-8 leading-relaxed">
          Create an account or sign in to get started.
        </p>
        <button
          onClick={() => goTo("signup")}
          className="w-full bg-brand-gold text-[#0d0d0c] font-bold border-0 rounded px-4 py-3.5 text-[0.8rem] tracking-[0.18em] uppercase hover:bg-brand-gold/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Account
        </button>
        <div className="h-px bg-white/[0.05] my-2" />
        <button
          onClick={() => goTo("login")}
          className="w-full bg-transparent text-text-muted border border-white/20 rounded-sm px-7 py-3.5 text-[0.75rem] tracking-[0.1em] uppercase hover:border-white/40 hover:text-text-primary transition-colors"
        >
          Already have an account? Sign in
        </button>
      </div>
    </div>
  );
}
